import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ProductDetails } from '@web-solutions/core/store/billing/selectors';

import { PaymentSystem } from '../../../constants';

import type { SdkInstance, CardPaymentResponse } from '../../truegate-sdk';
import { setPending } from '../../../../core/store/billing/actions';

import { CardForm } from './form';
import getLocale from '../../../../core/localization/get-locale';
import { useStore } from '../../../../../apps/virtual-brainary/src/onboarding/store/store';

interface CardElementProps {
  isDarkMode?: boolean;
  orderDetails: any;
  formStyles?: React.CSSProperties;
  options: any;
  buttonTitle?: string;
  product: ProductDetails;
  onReady?: (e: any) => void;
  onSubmit?: (e: any) => void;
  onUserInfoChange?: (e: any) => void;
  onSuccess?: (e: any) => void;
  onError?: (e: any) => void;
}

export const CardElement: React.FC<CardElementProps> = ({
  isDarkMode,
  options,
  buttonTitle,
  product,
  onReady,
  onSubmit,
  onUserInfoChange,
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch();
  const productId = product?.truegateId || product?.productCode || 'no product';

  const intent =
    options?.orderDetails?.products?.[productId] ||
    options?.orderDetails?.one_time_products?.[productId] ||
    options?.orderDetails;

  const [isLoading, setIsLoading] = useState(false);
  const [cardHolderName, setCardHolderName] = useState('');
  const [isCardNumberValid, setIsCardNumberValid] = useState(true);
  const [isExpirationValid, setIsExpirationValid] = useState(true);
  const [isSecurityCodeValid, setIsSecurityCodeValid] = useState(true);
  const [isCardHolderNameValid, setIsCardHolderNameValid] = useState(true);
  const cardPaymentRef = useRef<CardPaymentResponse | null>(null);
  const intentIdRef = useRef<string | null>(null);
  const cardHolderNameInputRef = useRef<HTMLInputElement | null>(null);
  const realEmail = useStore((state) => state.email);
  // Deferred-email flow: the customer is created with a synthetic address, so the
  // form must render (and charge) before the user has given a real email.
  const anonEmail = useStore((state) => state.anonEmail);
  const email = realEmail || anonEmail;
  const submittedRef = useRef(false);

  const formFieldsValidation = useRef<{
    [key: string]: {
      isValid: boolean;
    };
  }>({
    cardNumber: { isValid: false },
    expiration: { isValid: false },
    securityCode: { isValid: false },
  });

  useEffect(() => {
    const currentIntentId = intent?.transactionId;
    if (intentIdRef.current === currentIntentId) {
      console.log(
        'Card payment already initialized for this intent, skipping...'
      );
      return;
    }

    setCardHolderName('');
    formFieldsValidation.current.cardNumber.isValid = true;
    formFieldsValidation.current.expiration.isValid = true;
    formFieldsValidation.current.securityCode.isValid = true;
    intentIdRef.current = currentIntentId;

    const init = async () => {
      const ID = 'CARD_PAYMENT_TRUEGATE';

      //@ts-ignore
      const sdkInstance: SdkInstance = new window.truegateSdk({
        id: ID,
        transactionId: intent.transactionId,
        env: process.env.REACT_APP_TRUEGATE_ENV as any,
      });

      sdkInstance.on('CARD_PAYMENT_READY' as any, () => {
        onReady?.({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on('CARD_NUMBER_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.cardNumber.isValid = true;
        }
      });

      sdkInstance.on('CARD_NUMBER_BLUR' as any, () => {
        if (submittedRef.current) {
          console.log(
            'CARD_NUMBER_BLUR',
            formFieldsValidation.current.cardNumber.isValid
          );
          setIsCardNumberValid(formFieldsValidation.current.cardNumber.isValid);
        }
      });

      sdkInstance.on('CARD_NUMBER_VALIDATION_RESULT' as any, (payload: any) => {
        formFieldsValidation.current.cardNumber.isValid =
          payload.details.isValid;
      });

      sdkInstance.on('CARD_EXPIRATION_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.expiration.isValid = true;
        }
      });

      sdkInstance.on('CARD_EXPIRATION_BLUR' as any, () => {
        if (submittedRef.current) {
          setIsExpirationValid(formFieldsValidation.current.expiration.isValid);
        }
      });
      sdkInstance.on(
        'CARD_EXPIRATION_VALIDATION_RESULT' as any,
        (payload: any) => {
          formFieldsValidation.current.expiration.isValid =
            payload.details.isValid;
        }
      );

      sdkInstance.on('CARD_SECURITY_CODE_BLUR' as any, () => {
        if (submittedRef.current) {
          setIsSecurityCodeValid(
            formFieldsValidation.current.securityCode.isValid
          );
        }
      });

      sdkInstance.on('CARD_SECURITY_CODE_INPUT' as any, () => {
        if (!submittedRef.current) {
          formFieldsValidation.current.securityCode.isValid = true;
        }
      });

      sdkInstance.on(
        'CARD_SECURITY_CODE_VALIDATION_RESULT' as any,
        (payload: any) => {
          formFieldsValidation.current.securityCode.isValid =
            payload.details.isValid;
        }
      );

      sdkInstance.on('PAYMENT_ERROR' as any, (payload: any) => {
        console.log('PAYMENT_ERROR', payload);
        setIsLoading(false);
        dispatch(setPending(false));
        onError?.({
          method: 'card',
          price_id: productId,
          paymentSystem: PaymentSystem.TRUEGATE,
          ...payload,
        });
      });

      sdkInstance.on('PAYMENT_STATUS' as any, (payload: any) => {
        if (
          payload.channel === 'CARD_PAYMENT' &&
          payload.details.status === 'FAILED'
        ) {
          setIsLoading(false);
          dispatch(setPending(false));
          onError?.({
            method: 'card',
            price_id: productId,
            paymentSystem: PaymentSystem.TRUEGATE,
            ...payload,
          });
        }
        if (
          payload.details.status === 'PENDING' &&
          payload.channel === 'CARD_PAYMENT'
        ) {
          dispatch(setPending(true));
        }
        if (
          payload.details.status === 'SUCCESS' &&
          payload.channel === 'CARD_PAYMENT'
        ) {
          setIsLoading(false);
          dispatch(setPending(false));
          onSuccess?.({
            amount: product.amount,
            currency: product.currency,
            email: email,
            customer_email: email,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            paymentSystem: PaymentSystem.TRUEGATE,
            method: 'card',
            price_id: productId,
          });
        }
      });

      sdkInstance.on('CARD_PAYMENT_CARD_DETAILS' as any, (payload: any) => {
        onUserInfoChange?.({
          card_bank: payload.details?.bank,
          card_brand: payload.details?.provider,
          card_last4: payload.details?.last4,
        });
      });

      sdkInstance.on('CARD_SECURITY_CODE_COMPLETE' as any, (payload: any) => {
        if (payload.details.isCompleted) {
          cardHolderNameInputRef.current?.focus();
        }
      });

      await sdkInstance.init();

      const cardPayment = await sdkInstance.initCardPayment({
        cardNumberId: `card_number_id_${intent?.transactionId}`,
        expirationId: `expiration_id_${intent?.transactionId}`,
        securityCodeId: `security_code_id_${intent?.transactionId}`,
        options: {
          PLACEHOLDER_CARD_NUMBER: '1234 1234 1234 1234',
          PLACEHOLDER_EXPIRATION: 'MM / YY',
          PLACEHOLDER_SECURITY_CODE: 'CVV',
          FONT_NAME: 'OPEN SANS' as any,
          FONT_SIZE: '16px',
          LINE_HEIGHT: '24px',
          FONT_WEIGHT: '400',
          COLOR: '#000000',
        },
      });

      cardPaymentRef.current = cardPayment;
    };

    if (intent && currentIntentId) {
      init();
    }

    return () => {
      cardPaymentRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.transactionId]);

  const checkValidation = () => {
    const isCardHolderValid = !!cardHolderName.trim();
    const isCardNumberValid = formFieldsValidation.current.cardNumber.isValid;
    const isExpirationValid = formFieldsValidation.current.expiration.isValid;
    const isSecurityCodeValid = formFieldsValidation.current.securityCode.isValid;

    setIsCardHolderNameValid(isCardHolderValid);
    setIsCardNumberValid(isCardNumberValid);
    setIsExpirationValid(isExpirationValid);
    setIsSecurityCodeValid(isSecurityCodeValid);

    return isCardHolderValid && isCardNumberValid && isExpirationValid && isSecurityCodeValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    submittedRef.current = true;

    if (!checkValidation()) {
      return;
    }

    if (!cardPaymentRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      dispatch(setPending(true));
      await onSubmit?.({
        amount: product.amount,
        currency: product.currency,
        email: email,
        customer_email: email,
        subscription_id: productId,
        transaction_id: intent.transactionId,
        paymentSystem: PaymentSystem.TRUEGATE,
        method: 'card',
        price_id: productId,
      });
      await cardPaymentRef.current.submit({
        cardHolderName: cardHolderName.trim(),
      });
    } catch (error: any) {
      console.error('Card payment submit error:', error);
      onError?.({
        paymentSystem: PaymentSystem.TRUEGATE,
        price_id: productId,
        method: 'card',
        ...error,
      });
      setIsLoading(false);
      dispatch(setPending(false));
    }
  };

  const handleCardHolderNameBlur = (e: any) => {
    if (submittedRef.current) {
      setIsCardHolderNameValid(!!cardHolderName.trim());
    }
  };

  const handleCardHolderNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCardHolderName(e.target.value);
  };

  if (!intent || !email) {
    return null;
  }

  const isDisabled = submittedRef.current
    ? isLoading ||
      !(
        cardHolderName.trim() &&
        isCardNumberValid &&
        isExpirationValid &&
        isSecurityCodeValid
      )
    : isLoading;

  const locale = getLocale();

  return (
    <CardForm
      isDarkMode={isDarkMode}
      id={intent?.transactionId}
      isDisabled={isDisabled}
      isCardNumberValid={isCardNumberValid}
      isExpirationValid={isExpirationValid}
      isSecurityCodeValid={isSecurityCodeValid}
      cardHolderName={cardHolderName}
      isCardHolderNameValid={isCardHolderNameValid}
      cardHolderNameInputRef={cardHolderNameInputRef}
      buttonTitle={buttonTitle}
      onSubmit={handleSubmit}
      handleCardHolderNameBlur={handleCardHolderNameBlur}
      handleCardHolderNameChange={handleCardHolderNameChange}
      loading={isLoading}
    />
  );
};
